// components/Navbar.js
import React, { useState, useEffect } from 'react';
import { 
    AppBar, Toolbar, Typography, Button, Container, Box, 
    Badge, IconButton, Drawer, List, ListItem, ListItemText,
    useMediaQuery, useTheme, Menu, MenuItem, TextField,
    InputAdornment
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

function Navbar() {
    const [cartCount, setCartCount] = useState(0);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchAnchor, setSearchAnchor] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    // Load cart count from localStorage
    const loadCartCount = () => {
        try {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');
            const totalItems = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
            setCartCount(totalItems);
        } catch (error) {
            console.error('Error loading cart:', error);
            setCartCount(0);
        }
    };

    useEffect(() => {
        loadCartCount();
        const handleCartUpdate = () => loadCartCount();
        const handleStorageChange = (e) => {
            if (e.key === 'cart') loadCartCount();
        };

        window.addEventListener('cartUpdated', handleCartUpdate);
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('cartUpdated', handleCartUpdate);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const handleSearchOpen = (event) => setSearchAnchor(event.currentTarget);
    const handleSearchClose = () => setSearchAnchor(null);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
            setSearchQuery('');
            handleSearchClose();
        }
    };

    const menuItems = [
        { text: 'Home', path: '/' },
        { text: 'Products', path: '/products' },
        { text: 'About', path: '/about' },
        { text: 'Contact', path: '/contact' },
    ];

    const categoryMenu = [
        { text: 'All Products', path: '/products' },
        { text: 'Supplements', path: '/products?category=supplements' },
        { text: 'Vitamins', path: '/products?category=vitamins' },
        { text: 'Wellness', path: '/products?category=wellness' },
        { text: 'New Arrivals', path: '/products?new=true' },
        { text: 'Best Sellers', path: '/products?bestseller=true' },
        { text: 'On Sale', path: '/products?sale=true' },
    ];

    const drawer = (
        <Box sx={{ width: 280, bgcolor: '#1a1a2e', height: '100%' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                    🛍️ MyProducts
                </Typography>
            </Box>
            <List>
                {menuItems.map((item) => (
                    <ListItem 
                        button 
                        key={item.text} 
                        component={Link} 
                        to={item.path}
                        onClick={handleDrawerToggle}
                        sx={{ 
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <ListItemText primary={item.text} />
                    </ListItem>
                ))}
                <ListItem sx={{ color: 'white', fontWeight: 'bold', borderTop: '1px solid rgba(255,255,255,0.1)', mt: 2, pt: 2 }}>
                    <ListItemText primary="Categories" />
                </ListItem>
                {categoryMenu.map((item) => (
                    <ListItem 
                        button 
                        key={item.text} 
                        component={Link} 
                        to={item.path}
                        onClick={handleDrawerToggle}
                        sx={{ 
                            color: 'white',
                            pl: 4,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <ListItemText primary={item.text} />
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <>
            {/* Top Bar - License & Trust Badge */}
            <Box sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #e9ecef', py: 0.5 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#6c757d' }}>
                            License No: BU202504990
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                                🇺🇸 Authentic • Trusted • Wellness
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Main Navbar */}
            <AppBar position="sticky" color="primary" sx={{ zIndex: 9999, bgcolor: '#1a1a2e' }}>
                <Container maxWidth="lg">
                    <Toolbar sx={{ 
                        justifyContent: 'space-between', 
                        py: 1, 
                        minHeight: { xs: 56, sm: 64 },
                        flexWrap: 'wrap'
                    }}>
                        {/* Logo */}
                        <Typography 
                            variant="h6" 
                            component={Link} 
                            to="/" 
                            sx={{ 
                                textDecoration: 'none', 
                                color: 'white',
                                fontWeight: 'bold',
                                '&:hover': { opacity: 0.8 },
                                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' }
                            }}
                        >
                            🛍️ MyProducts
                        </Typography>

                        {/* Desktop Search */}
                        {!isMobile && (
                            <Box component="form" onSubmit={handleSearch} sx={{ flex: 1, maxWidth: 400, mx: 2 }}>
                                <TextField
                                    size="small"
                                    placeholder="Search for products..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{
                                        width: '100%',
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            borderRadius: 2,
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                        },
                                        '& .MuiInputBase-input': { color: 'white' },
                                        '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.6)' }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Button 
                                                    type="submit" 
                                                    variant="contained" 
                                                    size="small"
                                                    sx={{ 
                                                        bgcolor: '#667eea',
                                                        '&:hover': { bgcolor: '#5a6fd6' },
                                                        borderRadius: 2,
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    Search
                                                </Button>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Box>
                        )}

                        {/* Desktop Menu */}
                        {!isMobile && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <IconButton 
                                    component={Link} 
                                    to="/account"
                                    sx={{ color: 'white' }}
                                    title="Account"
                                >
                                    <PersonIcon />
                                </IconButton>
                                <IconButton 
                                    component={Link} 
                                    to="/compare"
                                    sx={{ color: 'white' }}
                                    title="Compare"
                                >
                                    <CompareArrowsIcon />
                                </IconButton>
                                <IconButton 
                                    component={Link} 
                                    to="/wishlist"
                                    sx={{ color: 'white' }}
                                    title="Wishlist"
                                >
                                    <FavoriteBorderIcon />
                                </IconButton>
                                <IconButton 
                                    component={Link} 
                                    to="/cart"
                                    sx={{ 
                                        color: 'white',
                                        '&:hover': { transform: 'scale(1.1)' },
                                    }}
                                >
                                    <Badge 
                                        badgeContent={cartCount} 
                                        color="error"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.7rem',
                                                minWidth: 18,
                                                height: 18,
                                                borderRadius: 9,
                                            }
                                        }}
                                    >
                                        <ShoppingCartIcon />
                                    </Badge>
                                </IconButton>
                            </Box>
                        )}

                        {/* Mobile Menu - Cart + Hamburger */}
                        {isMobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton 
                                    component={Link} 
                                    to="/cart"
                                    sx={{ color: 'white' }}
                                >
                                    <Badge 
                                        badgeContent={cartCount} 
                                        color="error"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.7rem',
                                                minWidth: 18,
                                                height: 18,
                                                borderRadius: 9,
                                            }
                                        }}
                                    >
                                        <ShoppingCartIcon />
                                    </Badge>
                                </IconButton>
                                <IconButton
                                    color="inherit"
                                    edge="end"
                                    onClick={handleDrawerToggle}
                                    sx={{ color: 'white' }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Toolbar>

                    {/* Category Navigation Bar */}
                    {!isMobile && (
                        <Box sx={{ 
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                            py: 0.5,
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 2,
                            flexWrap: 'wrap'
                        }}>
                            {categoryMenu.map((item) => (
                                <Button
                                    key={item.text}
                                    component={Link}
                                    to={item.path}
                                    sx={{
                                        color: 'rgba(255,255,255,0.8)',
                                        fontSize: '0.85rem',
                                        textTransform: 'none',
                                        '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                                        px: 2,
                                        py: 0.5
                                    }}
                                >
                                    {item.text}
                                </Button>
                            ))}
                        </Box>
                    )}
                </Container>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { 
                        bgcolor: '#1a1a2e',
                        width: 280,
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
}

export default Navbar;